import { css } from '@emotion/react'
import { Button, Cell, Grid } from 'bold-ui'
import { useSession } from 'components/auth/useSession'
import { PageContent } from 'components/layout/PageContent'
import { useFlags } from 'config/useFlagsContext'
import { TipoAcompanhamentoEnum } from 'graphql/types.generated'
import qs from 'qs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation, useRouteMatch } from 'react-router'
import { grupoCboVisualizarDetalhesEncaminhamentosNoAcompPreNatal } from 'view/cidadao/authorization/acessos'
import { CheckJustificativaAcessoProntuario } from 'view/prontuario/justificativa/CheckJustificativaAcessoProntuario'

import { AcompanhamentosRootView } from './AcompanhamentosRootView'
import { getAcompanhamentosSideMenuItems } from './getAcompanhamentosSideMenuItems'
import { AcompanhamentosUrlParams, ExternalPaths, redirectMapping } from './model-acompanhamentos'
import { AcompanhamentosSideMenu } from './side-menu/AcompanhamentosSideMenu'

export const ACOMPANHAMENTOS_URL = 'acompanhamentos'

interface AcompanhamentosViewProps {
  cidadaoId: ID
  prontuarioId: ID
  atendimentoId?: ID
  isGestante: boolean
  isPuerpera: boolean
  isVisualizacaoProntuario: boolean
  isEscutaInicial?: boolean
  dataReferencia: Instant
  headerRef?: React.MutableRefObject<HTMLDivElement | undefined>
}

export function AcompanhamentosView(props: Readonly<AcompanhamentosViewProps>) {
  const {
    cidadaoId,
    prontuarioId,
    atendimentoId,
    isGestante,
    isPuerpera,
    isVisualizacaoProntuario,
    dataReferencia,
    headerRef,
    isEscutaInicial = false,
  } = props
  const styles = createStyles(isVisualizacaoProntuario)

  const match = useRouteMatch()
  const history = useHistory()
  const location = useLocation()
  const urlParams: AcompanhamentosUrlParams = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  })

  const { hasCboAuth } = useSession()

  const { PILOTO_VINCULACAO_CUIDADO_ENABLED } = useFlags()

  const menuItems = useMemo(
    () =>
      getAcompanhamentosSideMenuItems({
        isPreNatalVisible: isGestante,
        isPuerperioVisible: isPuerpera,
        isVinculacaoCuidadoEnabled: PILOTO_VINCULACAO_CUIDADO_ENABLED,
        hasCboAuth: hasCboAuth(grupoCboVisualizarDetalhesEncaminhamentosNoAcompPreNatal),
      }),
    [isGestante, isPuerpera, PILOTO_VINCULACAO_CUIDADO_ENABLED, hasCboAuth]
  )

  const initial = useMemo((): TipoAcompanhamentoEnum => {
    const redirect = urlParams.redirect
    if (redirect && redirectMapping[redirect]) {
      return redirectMapping[redirect]
    }

    return isPuerpera ? TipoAcompanhamentoEnum.PUERPERIO : TipoAcompanhamentoEnum.PRE_NATAL
  }, [urlParams.redirect, isPuerpera])

  const [selectedMenu, setSelectedMenu] = useState<TipoAcompanhamentoEnum>(initial)

  const basePath = match.url.replace(`/${ACOMPANHAMENTOS_URL}`, '')
  const cidadaoIdUrl = !isVisualizacaoProntuario ? `/${cidadaoId}` : ''

  const externalPaths = {
    basePath,
    vacinacaoPath: `${basePath}/vacinacao${location.search}`,
    agendamentosPath: `${basePath}/agendamentos${cidadaoIdUrl}${location.search}`,
    historicoPath: `${basePath}/historico${location.search}`,
    antropometriaPath: `${basePath}/soap/historicoMedicoes/antropometria`,
  } as ExternalPaths

  const handleRouteChange = useCallback(
    (selectedMenu: TipoAcompanhamentoEnum) => {
      history.push(`${basePath}/${menuItems[selectedMenu].url}${location.search}`)
    },
    [basePath, location.search, menuItems, history]
  )

  const handleSoapButtonClick = () => {
    history.push(`${basePath}/soap`)
  }

  useEffect(() => {
    handleRouteChange(selectedMenu)
  }, [handleRouteChange, selectedMenu])

  return (
    <PageContent fluid type='filled'>
      <CheckJustificativaAcessoProntuario
        prontuarioId={prontuarioId}
        cidadaoId={cidadaoId}
        basePath={basePath}
        disabled={!!atendimentoId || !isVisualizacaoProntuario}
      >
        <Grid gap={2}>
          <Cell size={3} style={styles.sideCell}>
            <AcompanhamentosSideMenu
              basePath={basePath}
              headerRef={headerRef}
              isVisualizacaoProntuario={isVisualizacaoProntuario}
              items={menuItems}
              selectedMenu={selectedMenu}
              setSelectedMenu={setSelectedMenu}
            />
            {!isVisualizacaoProntuario && !isEscutaInicial && (
              <Button onClick={handleSoapButtonClick} style={styles.button}>
                Ir para o SOAP
              </Button>
            )}
          </Cell>
          <Cell size={9}>
            <AcompanhamentosRootView
              prontuarioId={prontuarioId}
              atendimentoId={atendimentoId}
              cidadaoId={cidadaoId}
              dataReferencia={dataReferencia}
              externalPaths={externalPaths}
              isVisualizacaoProntuario={isVisualizacaoProntuario}
              isEscutaInicial={isEscutaInicial}
              redirectRoute={`${basePath}/${menuItems[selectedMenu].url}${location.search}`}
            />
          </Cell>
        </Grid>
      </CheckJustificativaAcessoProntuario>
    </PageContent>
  )
}

const createStyles = (isVisualizacao: boolean) => ({
  sideCell: css`
    position: sticky;
    align-items: start;
    top: ${isVisualizacao ? '1rem' : '7.5rem'};
    height: 100%;
  `,

  button: css`
    width: 100%;
    margin-top: 1rem;
  `,
})
